import { Platform, StyleSheet } from "react-native";

const inter = (weight: "400" | "500" | "600" | "700") =>
  Platform.select({
    ios: {
      fontFamily:
        weight === "400"
          ? "Inter-Regular"
          : weight === "500"
          ? "Inter-Medium"
          : weight === "600"
          ? "Inter-SemiBold"
          : "Inter-Bold",
      fontWeight: weight as any,
    },
    android: {
      fontFamily:
        weight === "400"
          ? "Inter-Regular"
          : weight === "500"
          ? "Inter-Medium"
          : weight === "600"
          ? "Inter-SemiBold"
          : "Inter-Bold",
      fontWeight: weight as any,
    },
    default: { fontFamily: "Inter, sans-serif", fontWeight: weight as any },
  }) as { fontFamily: string; fontWeight: any };

export default StyleSheet.create({
  // ── Overlay ───────────────────────────────────────────────────────────────
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(3, 18, 45, 0.55)",
  },

  // ── Modal positioning wrapper ─────────────────────────────────────────────
  modalWrap: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  modal: {
    backgroundColor: "#ffffff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 780,
    // On web maxHeight works with flex children; on iOS native we need an
    // explicit height so the flex:1 body can expand between header and footer.
    ...Platform.select({
      web: { maxHeight: "90%" } as any,
      default: { height: "70%" },
    }),
    overflow: "hidden",
    ...Platform.select({
      web: { boxShadow: "0 8px 48px rgba(0,30,70,0.18)" } as any,
      default: {
        shadowColor: "#001E46",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.18,
        shadowRadius: 24,
        elevation: 12,
      },
    }),
  },

  // ── Header ────────────────────────────────────────────────────────────────
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
  },
  modalTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  modalTitle: {
    fontSize: 16,
    color: "#1a1a1a",
    ...inter("700"),
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 100,
    alignItems: "center",
    justifyContent: "center",
  },
  closeBtnPressed: { backgroundColor: "#e7effb" },

  // ── Body ──────────────────────────────────────────────────────────────────
  modalBody: { flex: 1 },
  modalBodyContent: {
    padding: 24,
    gap: 0,
  },

  // ── Form layout ───────────────────────────────────────────────────────────
  formRow: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 0,
    flexWrap: "wrap",
  },
  formCol: { flex: 1, minWidth: 200 },

  // ── Field ─────────────────────────────────────────────────────────────────
  fieldWrap: { marginBottom: 16, zIndex: 1 },
  // Elevate the entire field when its dropdown is open so the menu clears siblings
  fieldWrapOpen: { zIndex: 200 },
  // Wrapper that establishes the positioning context for the absolute menu
  selectWrap: { position: "relative" },
  labelRow: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 4,
    marginBottom: 6,
  },
  label: {
    fontSize: 12,
    color: "#3a3a3a",
    marginBottom: 6,
    ...inter("600"),
  },
  req: { color: "#C12C01" },
  optional: { fontSize: 11, color: "#818181", ...inter("400") },
  helpText: { fontSize: 11, color: "#818181", marginTop: 5, ...inter("400") },
  charCounter: {
    fontSize: 11,
    color: "#818181",
    textAlign: "right",
    marginTop: 4,
    ...inter("400"),
  },
  overflowWarningText: {
    fontSize: 11,
    color: "#b45309",
    marginTop: 5,
    ...inter("500"),
  },

  // ── TextInput ─────────────────────────────────────────────────────────────
  textInput: {
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 13,
    color: "#2d2d2d",
    backgroundColor: "#fafafa",
    ...inter("400"),
  },
  textInputFocused: {
    borderColor: "#005CBC",
    backgroundColor: "#ffffff",
  },
  textArea: {
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 13,
    color: "#2d2d2d",
    backgroundColor: "#fafafa",
    minHeight: 96,
    maxHeight: 220,
    textAlignVertical: "top",
    ...inter("400"),
  },

  // ── Inline Select ─────────────────────────────────────────────────────────
  selectTrigger: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 9,
    backgroundColor: "#fafafa",
  },
  selectTriggerPressed: { backgroundColor: "#f0f4fa" },
  // When open: highlight border only — popup floats separately so keep full radius/border
  selectTriggerOpen: {
    borderColor: "#005CBC",
    backgroundColor: "#EEF5FF",
  },
  selectText: { fontSize: 13, color: "#2d2d2d", flex: 1, ...inter("400") },
  // Inline menu — used only for TicketSearch (still inline); popup dropdowns use popupStyles
  selectMenu: {
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    overflow: "hidden",
    marginBottom: 4,
  },
  selectMenuItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  selectMenuItemActive: { backgroundColor: "#EEF5FF" },
  selectMenuItemPressed: { backgroundColor: "#f3f7fd" },
  selectMenuItemText: { fontSize: 13, color: "#2d2d2d", flex: 1, ...inter("400") },
  selectMenuItemTextActive: { color: "#034895", ...inter("600") },

  // ── Target Audience section ───────────────────────────────────────────────
  audienceSection: {
    backgroundColor: "#F5F8FB",
    borderRadius: 10,
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 13,
    color: "#2d2d2d",
    marginBottom: 14,
    ...inter("700"),
  },


  // ── Send Via checkboxes ───────────────────────────────────────────────────
  checkGroup: { flexDirection: "row", gap: 12, flexWrap: "wrap" },
  checkRow: { flexDirection: "row", alignItems: "center", gap: 6 },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: "#C0C0C0",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ffffff",
  },
  checkboxChecked: {
    backgroundColor: "#034895",
    borderColor: "#034895",
  },
  checkLabel: { fontSize: 13, color: "#3a3a3a", ...inter("400") },

  // ── Date/Time field ───────────────────────────────────────────────────────
  dateFieldWrap: { flex: 1 },
  dateFieldLabel: { fontSize: 11, color: "#818181", marginBottom: 4, ...inter("500") },

  // Pressable trigger (iOS / Android)
  dateTrigger: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 9,
    backgroundColor: "#fafafa",
  },
  dateTriggerPressed: { backgroundColor: "#f0f4fa" },
  dateTriggerOpen: { borderColor: "#005CBC", backgroundColor: "#EEF5FF" },
  dateTriggerText: { fontSize: 13, color: "#2d2d2d", flex: 1, ...inter("400") },

  // ── Date row (wraps two DateTimeField side by side) ───────────────────────
  dateRow: { flexDirection: "row", gap: 8 },
  dateInput: { flex: 1 },

  // ── Recipients line ───────────────────────────────────────────────────────
  recipientRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 10,
  },
  recipientText: { fontSize: 12, color: "#034895", ...inter("600") },

  // ── Footer ────────────────────────────────────────────────────────────────
  modalFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#EBEBEB",
  },
  footerBtnGhost: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 100,
    paddingHorizontal: 16,
    paddingVertical: 9,
  },
  footerBtnGhostPressed: { backgroundColor: "#e7effb" },
  footerBtnGhostText: { fontSize: 13, color: "#4b4b4b", ...inter("500") },

  footerBtnSecondary: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 100,
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    backgroundColor: "#ffffff",
  },
  footerBtnSecondaryPressed: { backgroundColor: "#f3f7fd" },
  footerBtnSecondaryText: { fontSize: 13, color: "#034895", ...inter("600") },

  footerBtnPrimary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderRadius: 100,
    paddingHorizontal: 18,
    paddingVertical: 9,
    backgroundColor: "#034895",
  },
  footerBtnPrimaryPressed: { backgroundColor: "#01346F" },
  footerBtnPrimaryText: { fontSize: 13, color: "#ffffff", ...inter("600") },

  // ── Confirm-leave overlay ─────────────────────────────────────────────────
  confirmOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(255,255,255,0.92)",
    zIndex: 100,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 12,
    padding: 24,
  },
  confirmCard: {
    alignItems: "center",
    maxWidth: 380,
    width: "100%" as any,
  },
  confirmTitle: {
    fontSize: 17,
    color: "#1a1a1a",
    marginBottom: 8,
    ...inter("700"),
  },
  confirmBody: {
    fontSize: 13,
    color: "#585858",
    textAlign: "center",
    lineHeight: 20,
    marginBottom: 24,
    ...inter("400"),
  },
  confirmActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap" as any,
    justifyContent: "center",
  },
  confirmBtnStay: {
    paddingHorizontal: 18,
    paddingVertical: 9,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    backgroundColor: "#ffffff",
  },
  confirmBtnStayText: { fontSize: 13, color: "#4b4b4b", ...inter("500") },
  confirmBtnDiscard: {
    paddingHorizontal: 18,
    paddingVertical: 9,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#F5C0B0",
    backgroundColor: "#FFF5F3",
  },
  confirmBtnDiscardText: { fontSize: 13, color: "#C12C01", ...inter("500") },
  confirmBtnDraft: {
    flexDirection: "row" as any,
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 18,
    paddingVertical: 9,
    borderRadius: 100,
    backgroundColor: "#034895",
  },
  confirmBtnDraftText: { fontSize: 13, color: "#ffffff", ...inter("600") },

  // ── Field footer (error + char counter on same line) ──────────────────────
  fieldFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 4,
    minHeight: 16,
  },

  // ── Validation error display ───────────────────────────────────────────────
  errorRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flex: 1,
  },
  errorText: { fontSize: 11, color: "#C12C01", ...inter("500") },

  // ── Error border on input ─────────────────────────────────────────────────
  textInputError: { borderColor: "#C12C01", backgroundColor: "#FFF5F3" },

  // ── Toggle switch ─────────────────────────────────────────────────────────
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingVertical: 6,
  },
  toggleRowDisabled: { opacity: 0.45 },
  toggleTrack: {
    width: 36,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#D0D0D0",
    justifyContent: "center",
    paddingHorizontal: 2,
    flexShrink: 0,
  },
  toggleTrackOn: { backgroundColor: "#034895" },
  toggleThumb: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: "#ffffff",
  },
  toggleThumbOn: { alignSelf: "flex-end" },
  toggleLabel: { fontSize: 13, color: "#2d2d2d", ...inter("500") },
  toggleLabelDisabled: { color: "#a8a8a8" },
  toggleHint: { fontSize: 11, color: "#818181", marginTop: 2, ...inter("400") },

  // ── Ticket search ─────────────────────────────────────────────────────────
  ticketSearchWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: "#fafafa",
  },
  ticketSearchInput: {
    flex: 1,
    fontSize: 13,
    color: "#2d2d2d",
    ...inter("400"),
  },
  ticketOption: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  ticketOptionId: { fontSize: 12, color: "#034895", ...inter("600") },
  ticketOptionTitle: { fontSize: 12, color: "#585858", marginTop: 1, ...inter("400") },

  // ── Conditional ticket options block ──────────────────────────────────────
  ticketConditionals: {
    backgroundColor: "#F5F8FB",
    borderRadius: 8,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E3EDF8",
  },

  // ── All Users note ────────────────────────────────────────────────────────
  allUsersNote: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "#EEF5FF",
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  allUsersText: { fontSize: 13, color: "#034895", flex: 1, ...inter("400") },

  // ── Date auto note (when displayUntilClosed) ──────────────────────────────
  dateAutoNote: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#F5F8FB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: "#E3EDF8",
  },
  dateAutoText: { fontSize: 12, color: "#818181", flex: 1, ...inter("400") },
});
